import React, { useState } from 'react';
import {
  Box, Typography, Stack, Tabs, Tab, Paper, Grid, Button, Link,
  FormLabel, Checkbox, FormControlLabel, Select, MenuItem, Autocomplete, TextField, Switch
} from '@mui/material';

// Helper component for the form fields to reduce repetition
const FormField = ({ label, children, helperText }) => (
  <Box>
    <FormLabel label>
      {label}
      {label.endsWith('*') && <Typography component="span" color="error" sx={{ ml: 0.25 }}></Typography>}
    </FormLabel>
    <Box mt={0.5}>{children}</Box>
    {helperText && <Typography variant="caption" color="text.secondary">{helperText}</Typography>}
  </Box>
);

const PreferencesPage = () => {
  // Set the default active tab to "Preferences" (index 3)
  const [tabIndex, setTabIndex] = useState(3);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'white' }}>
      <Grid container spacing={4}>

        {/* Left Column - Form */}
        <Grid item xs={12} lg={7}>
          <Stack spacing={3}>
            <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} type="button">
              <Tab label="Personal Details" />
              <Tab label="Professional Details" />
              <Tab label="Education Info" />
              <Tab label="Preferences" />
            </Tabs>

            <Typography variant="body1" color="text.secondary">
              You can find <Link href="#">resume analyser</Link> integrated in Professional details
            </Typography>

            <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'yellow.main', borderRadius: 2, border: '1px solid #FEE2B3' }}>
              <Typography fontWeight="bold" color="secondary.main">How do you like Resume Builder?</Typography>
              <Typography variant="body2" color="secondary.light">Kindly provide your feedback on the resume builder to help us enhance your experience.</Typography>
            </Paper>

            <Stack spacing={3}>
              <Typography variant="h6" fontWeight="bold">Preferences</Typography>
              <Stack component="ul" spacing={1} sx={{ pl: '20px', m: 0 }}>
                  <Box component="li" sx={{ color: '#D97706', '&::marker': { color: '#D97706' } }}>
                    <Typography color="inherit">The information below will not be reflected in your Scaler resume.</Typography>
                  </Box>
                  <Box component="li" sx={{ color: '#D97706', '&::marker': { color: '#D97706' } }}>
                    <Typography color="inherit">Changes made to preference details will be reflected across all your resumes, as this is profile-specific.</Typography>
                  </Box>
              </Stack>
            </Stack>
            
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <FormField label="Preferred Locations *">
                        <Autocomplete options={[]} renderInput={(params) => <TextField {...params} placeholder="Enter your preferred locations" />} />
                        <FormControlLabel control={<Checkbox size="small" />} label={<Typography variant="body2" color="text.secondary">Any where in India</Typography>} sx={{mt: 1}}/>
                    </FormField>
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormField label="Job Role Type *">
                        <Autocomplete multiple defaultValue={['Full Time']} options={['Full Time', 'Part Time']} renderInput={(params) => <TextField {...params} />} />
                    </FormField>
                </Grid>
                 <Grid item xs={12} md={4}>
                    <FormField label="Preferred Role *">
                        <Autocomplete options={[]} renderInput={(params) => <TextField {...params} placeholder="Enter Your Preferred Role" />} />
                    </FormField>
                </Grid>
                 <Grid item xs={12} md={6}>
                    <FormField label="Company type preference *">
                        <Select fullWidth defaultValue="" displayEmpty>
                            <MenuItem value="" disabled>Select company type you want to apply</MenuItem>
                        </Select>
                    </FormField>
                </Grid>
                 <Grid item xs={12} md={6}>
                    <FormField label="Expected CTC (in Lakhs) *" helperText="Enter amount in lakhs. For eg, if amount is 50 LPA, enter 50.">
                        <Select fullWidth defaultValue="" displayEmpty>
                            <MenuItem value="" disabled>Select company type you want to apply</MenuItem>
                        </Select>
                    </FormField>
                </Grid>
            </Grid>

            <Stack spacing={3}>
                <Box>
                    <Typography variant="h6" fontWeight="bold">Notice Period</Typography>
                    <Grid container spacing={3} mt={0}>
                        <Grid item xs={12} md={6}>
                            <FormField label="Notice Period *">
                                <Select fullWidth defaultValue="" displayEmpty><MenuItem value="" disabled>Select notice period</MenuItem></Select>
                                <FormControlLabel control={<Checkbox size="small" />} label={<Typography variant="body2" color="text.secondary">Negotiable / Buyout available</Typography>} sx={{mt: 1}}/>
                            </FormField>
                        </Grid>
                    </Grid>
                </Box>
                 <Box>
                    <Typography variant="h6" fontWeight="bold">Total Years of Experience</Typography>
                     <Typography variant="body2" color="text.secondary">Include only relevant years of experience. Don't include internships or experiences in non tech domain.</Typography>
                    <Grid container spacing={3} mt={0}>
                        <Grid item xs={12} md={6}>
                            <FormField label="Years*">
                                <Autocomplete defaultValue="17 Years" options={['17 Years']} renderInput={(params) => <TextField {...params} />} />
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                             <FormField label="Months*">
                                <Autocomplete defaultValue="0 Months" options={['0 Months']} renderInput={(params) => <TextField {...params} />} />
                            </FormField>
                        </Grid>
                    </Grid>
                </Box>
                 <Box>
                    <Typography variant="h6" fontWeight="bold">Create Alert</Typography>
                    <FormControlLabel control={<Switch defaultChecked />} label={<Typography variant="body2" color="text.secondary">Notify me on email and whatsapp if any relevant job is added to Careers Hub dashboard as per my given preference</Typography>} />
                </Box>
                 <Box>
                    <Typography variant="h6" fontWeight="bold">Consent</Typography>
                    <FormControlLabel control={<Checkbox />} label={<Typography variant="body2" color="text.secondary">I give my consent to share my personal information with external recruiters for the purpose of enhancing job prospects</Typography>} />
                </Box>
            </Stack>
            
            <Box>
              <Button variant="contained" sx={{ px: 3, textTransform: 'none' }}>Update Preference</Button>
            </Box>

          </Stack>
        </Grid>

        {/* Right Column - Preview (Identical to previous components) */}
        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: 2, bgcolor: 'primary.main', borderRadius: 2, color: 'white', position: 'sticky', top: 24 }}>
            <Stack spacing={2} alignItems="center">
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                    <Button variant="outlined" sx={{color: 'white', borderColor: 'white'}}>Change Template</Button>
                    <Button variant="outlined" sx={{color: 'white', borderColor: 'white'}}>Resume Repository</Button>
                    <Button variant="outlined" sx={{color: 'white', borderColor: 'white'}}>Download Resume</Button>
                    <Select
                        defaultValue="Medium"
                        variant="outlined"
                        sx={{
                            color: 'white',
                            '.MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                            '.MuiSvgIcon-root': { color: 'white' },
                            height: '40px',
                            minWidth: 180
                        }}
                    >
                        <MenuItem value="Medium">Aa Font Size : Medium</MenuItem>
                    </Select>
                </Stack>
                <Typography variant="body2">Please save your details before downloading for changes to reflect in resume</Typography>
                <Paper sx={{ width: '100%', height: '80vh', bgcolor: 'white' }} />
            </Stack>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
};

export default PreferencesPage;